import { Dispatch, FC, SetStateAction, useCallback, useEffect, useState } from "react";
import isEqual from "lodash.isequal";
import {
  Button,
  Flex,
  Input,
  LegacyDatePicker,
  LegacyForm,
  Modal,
  Select,
  Space,
  Tooltip,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { QuestionCircleOutlined } from "@payping/icons";
import { createClientToken, editClientAccessToken, editClientToken, getClientTokenList } from "@payping/redux/actions";
import { SDKDateObject, dateFormats, isBeforeToday, toUtc, utcNow } from "@payping/utils/dateUtils";

import { useAppDispatch } from "@/utils/redux";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { isLocalIp, validIpOrRange } from "@/utils/validIp";
import { ConfirmModalProps } from "@/components/DashIpg/ConfirmModal";
import ModalConfirmGenerator from "@/components/DashIpg/ModalConfirmGenerator";
import { OtpCode } from "@/components/OtpCode";
import { TokenType } from "./TokenTypes";
import { SwitchRow } from "@/core/component";
import { TOKEN_SCOPES } from "@/core/consts";

interface FormRef {
  formModalData: TokenType;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>;
  setConfirmModalData: Dispatch<SetStateAction<ConfirmModalProps | undefined>>;
  modal: any;
}

interface FormType {
  isTest?: boolean;
  clientName?: string;
  allowedIPs?: string[];
  expireDate?: string | SDKDateObject;
  scopes?: string[];
  description?: string;
}

export const TokenFormModal: FC<FormRef> = ({
  formModalData,
  open,
  setOpen,
  setConfirmModalData,
  setConfirmModalOpen,
  modal,
}: FormRef) => {
  // =========================================
  // Init
  // =========================================
  const { t } = useAppTranslation();
  const dispatch = useAppDispatch();
  const [form] = LegacyForm.useForm<any>();
  const [hiddenScopes] = useState<string[]>(
    (formModalData.scopes || []).reduce(
      (acc: string[], scope) =>
        (TOKEN_SCOPES.ADMIN_SCOPES as unknown as string[]).includes(scope) ? [...acc, scope] : acc,
      [],
    ),
  );
  const [isOtpValid, setIsOtpValid] = useState(false);

  useEffect(() => {
    form.setFieldsValue(formModalData);
  }, [form, formModalData]);

  const hideModal = useCallback(() => {
    form.resetFields();
    setOpen(false);
  }, [form, setOpen]);

  // =========================================
  // Handler
  // =========================================
  const [, handleRenew] = useMessagedAsyncFn(
    async (values: FormType) => {
      const { scopes: formScopes, expireDate, ...otherValues } = values;
      await dispatch(
        editClientToken({
          ...otherValues,
          clientId: formModalData.clientId,
          expireDate: toUtc({ date: expireDate, toFormat: dateFormats.standard.utcDateTime }).string,
          scopes: formScopes?.filter((scope) => !hiddenScopes.includes(scope)),
        }),
      );
      const response = await dispatch(
        editClientAccessToken({
          clientId: formModalData.clientId,
        }),
      );

      if (response?.token) {
        setConfirmModalData({ showGuide: false, token: response?.token });
        setConfirmModalOpen(true);
        hideModal();
      } else {
        hideModal();
        modal.error({
          content: t("token.timeLimitError"),
          title: t("error.title"),
        });
      }
      await dispatch(getClientTokenList());
    },
    [dispatch, formModalData.clientId, hiddenScopes, hideModal, modal, setConfirmModalData, setConfirmModalOpen, t],
    undefined,
    // `success = false` because redirect to `DashboardRoutes.ipg.tokenShow` on successful action
    // `loading = false` because of `renewLoading`
    { loading: false, success: false },
  );
  // This block documented in `Token.md`
  const [{ loading: loadingSubmit }, onSubmit] = useMessagedAsyncFn(
    async (values: FormType) => {
      const { scopes: formScopes, expireDate, ...otherValues } = values;

      // [form] update operation
      if (formModalData.clientId) {
        const isScopesChanged = !isEqual(formModalData.scopes, values.scopes);
        const isExpireDateChanged = !isEqual(formModalData.expireDate, values.expireDate);
        const isClientNameChanged = formModalData.clientName !== values.clientName;
        const isTestModeChanged = formModalData.isTest !== values.isTest;
        const isIpChanged = !isEqual(formModalData.allowedIPs, values.allowedIPs);
        const todayPlusOneYear = toUtc({ date: utcNow().object }).object?.add(365 + 1, "days");
        const isTokenDurationBelowOneYear = todayPlusOneYear?.isAfter(toUtc({ date: values.expireDate }).object);
        const isAtLeastOneValueChange = [
          isScopesChanged,
          isExpireDateChanged,
          isClientNameChanged,
          isTestModeChanged,
          isIpChanged,
        ].some((s) => s);

        // every change lead to token renew except `description` changing
        if (isAtLeastOneValueChange) {
          const messages: string[] = [];
          if (
            [isScopesChanged, isExpireDateChanged, isClientNameChanged, isTestModeChanged, isIpChanged].some((s) => s)
          )
            messages.push(t("token.tokenRenewMessage"));
          if (isTokenDurationBelowOneYear) messages.push(t("token.tokenDurationMessage"));
          modal.confirm({
            cancelText: t("cancel"),
            content: <ModalConfirmGenerator messages={messages} />,
            mask: { closable: true },
            okText: t("ok"),
            onOk: async () => handleRenew(values),
          });
        } else {
          await dispatch(
            editClientToken({
              ...otherValues,
              clientId: formModalData.clientId,
              expireDate: toUtc({ date: expireDate, toFormat: dateFormats.standard.utcDateTime }).string,
              scopes: formScopes?.filter((scope) => !hiddenScopes.includes(scope)),
            }),
          );
          await dispatch(getClientTokenList());
          setOpen(false);
        }
      }

      // [form] create operation
      else {
        const result = await dispatch(
          createClientToken({
            ...values,
            expireDate: toUtc({ date: expireDate, toFormat: dateFormats.standard.utcDateTime }).string,
          }),
        );
        await dispatch(getClientTokenList());
        setConfirmModalData({ showGuide: result.isTest, token: result.token });
        setConfirmModalOpen(true);
        hideModal();
      }
    },
    [
      dispatch,
      formModalData.clientId,
      formModalData.clientName,
      formModalData.expireDate,
      formModalData.isTest,
      formModalData.scopes,
      formModalData.allowedIPs,
      handleRenew,
      hiddenScopes,
      hideModal,
      modal,
      setConfirmModalData,
      setConfirmModalOpen,
      setOpen,
      t,
    ],
    undefined,
    { useProgress: true },
  );

  // =========================================
  // Render
  // =========================================
  return (
    <Modal
      title={formModalData.clientId ? t("token.editToken") : t("token.addToken")}
      mask={{ closable: false }}
      open={open}
      okText={
        formModalData.clientId ? (
          <Button disabled={!isOtpValid} type={"primary"}>
            {t("token.editToken")}
          </Button>
        ) : (
          <Button type={"primary"}>{t("token.addToken")}</Button>
        )
      }
      footer={
        <Flex gap={"small"}>
          <Button type={"default"} onClick={() => hideModal()}>
            {t("return")}
          </Button>
          {formModalData.clientId ? (
            <Button disabled={!isOtpValid} type={"primary"} onClick={() => form.submit()}>
              {t("token.editToken")}
            </Button>
          ) : (
            <Button onClick={() => form.submit()} type={"primary"} disabled={!isOtpValid}>
              {t("token.addToken")}
            </Button>
          )}
        </Flex>
      }
      // onOk={() => form.submit()}
      onCancel={() => hideModal()}
      // for displaying form content when chat icon is visible on mobile
      classNames={{ container: "tw:max-lg:pb-20!" }}
    >
      <LegacyForm form={form as any} onFinish={onSubmit} layout={"vertical"}>
        <LegacyForm.Item name="isTest" hidden={Boolean(formModalData.clientId)} valuePropName={"checked"}>
          <SwitchRow
            loading={loadingSubmit}
            label={
              <Tooltip
                placement="leftBottom"
                title={
                  <ul>
                    <li key={0}>{t("token.attention1")}</li>
                    <br />
                    <li key={1}>{t("token.attention2")}</li>
                    <br />
                    <li key={1}>{t("token.attention3")}</li>
                    <br />
                    <li key={1}>{t("token.attention4")}</li>
                  </ul>
                }
              >
                <Space>
                  {t("token.testToken")}
                  <QuestionCircleOutlined width={"14px"} />
                </Space>
              </Tooltip>
            }
            checkedChildren={t("active")}
            unCheckedChildren={t("inactive")}
          />
        </LegacyForm.Item>
        <LegacyForm.Item label={t("token.clientName")} name="clientName" rules={[{ required: true }]}>
          <Input />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={t("token.allowedIps")}
          name="allowedIPs"
          dependencies={["scopes", "isTest"]}
          tooltip={t("token.allowedIpsHelp")}
          rules={[
            ({ getFieldValue }) => ({
              required:
                !getFieldValue("isTest") &&
                ((getFieldValue("scopes") as string[]) || []).some(
                  (scope) => scope.includes("pay") || scope.includes("write"),
                ),
            }),
            () => ({
              validator(_rule, value: string[]): Promise<void> {
                const invalidIp = (value || []).find((ip) => !validIpOrRange(ip));
                if (invalidIp) {
                  return Promise.reject(t("error.ip"));
                }
                const localIp = (value || []).find((ip) => isLocalIp(ip));
                return localIp ? Promise.reject(t("error.localIp")) : Promise.resolve();
              },
            }),
          ]}
        >
          <Select className={"tw:dir-ltr!"} placeholder={"8.8.8.8"} mode="tags" />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={t("expireDate")}
          name="expireDate"
          rules={[
            { required: true },
            () => ({
              validator(_, value) {
                if (value && isBeforeToday(value)) {
                  return Promise.reject(new Error(t("token.dateValidation")));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <LegacyDatePicker disabledDate={isBeforeToday} skeleton={loadingSubmit} futureYears={50} />
        </LegacyForm.Item>
        <LegacyForm.Item label={t("token.scopes")} name="scopes" rules={[{ required: true }]}>
          <Select
            mode="multiple"
            allowClear
            filterOption={(_, option) => (option?.value ? !hiddenScopes.includes(String(option?.value)) : false)}
          >
            {TOKEN_SCOPES.USER_SCOPES.map((scope) => (
              <Select.Option key={scope} value={scope}>
                {t(`scopes.${scope.replace(":", "")}`)}
              </Select.Option>
            ))}

            {hiddenScopes.map((scope) => (
              <Select.Option key={scope} value={scope} disabled>
                {t(`scopes.${scope.replace(":", "")}`)}
              </Select.Option>
            ))}
          </Select>
        </LegacyForm.Item>
        <LegacyForm.Item label={t("description")} name="description" rules={[{ required: true }]}>
          <Input.TextArea />
        </LegacyForm.Item>
        <OtpCode form={form} onVerify={(isVerified) => setIsOtpValid(isVerified)} />
      </LegacyForm>
    </Modal>
  );
};
